import { UploadFile } from "antd"
import { SurveyError } from "../../ui/error/SurveyError"
import { UpdateSurveyCompletionRequest } from "../../domain/useCases/surveys/updateSurveyCompletion/request"
import { UpdateSurveyCompletionResponse } from "../../domain/useCases/surveys/updateSurveyCompletion/response"
import { SurveyModel } from "../models/SurveyModel"
import { SurveySectionModel } from "../models/SurveySectionModel"
import { Endpoint } from "../network/endpoint"
import { HttpManager } from "./../network/interface/HttpManager"

export class SurveyService {
	private _http: HttpManager

	constructor({ HttpManager }: { HttpManager: HttpManager }) {
		this._http = HttpManager
	}

	public async createSurvey(survey: SurveyModel): Promise<SurveyModel> {
		try {
			const payload = {
				title: survey.title,
				title_en: survey.title_en,
				title_ca: survey.title_ca,
				description: survey.description,
				description_en: survey.description_en,
				description_ca: survey.description_ca,
				status: survey.status,
				image: survey.image,
				marketplaces: survey.marketplaces,
				micrositeIds: survey.microsites?.map(microsite => microsite.id),
				sectionIds: survey.sections?.map(section => section.id),
				questionIds: survey.sections?.flatMap(
					section => section.questions?.filter(question => question.enabled).map(question => question.id)
				),
				adminRecommendation: survey.adminRecommendation
			}
			const response = await this._http.requestNewApi({
				method: "POST",
				url: Endpoint.SURVEYS,
				headers: { "Content-Type": "multipart/form-data" },
				data: payload
			})
			return SurveyModel.fromRawJson(response.data)
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEY_CREATE_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async updateSurvey(survey: SurveyModel): Promise<SurveyModel> {
		try {
			const payload = {
				title: survey.title,
				title_en: survey.title_en,
				title_ca: survey.title_ca,
				description: survey.description,
				description_en: survey.description_en,
				description_ca: survey.description_ca,
				status: survey.status,
				image: survey.image,
				marketplaces: survey.marketplaces,
				micrositeIds: survey.microsites?.map(microsite => microsite.id),
				sectionIds: survey.sections?.map(section => section.id),
				questionIds: survey.sections?.flatMap(
					section => section.questions?.filter(question => question.enabled).map(question => question.id)
				),
				adminRecommendation: survey.adminRecommendation
			}
			console.log("[SurveyService] Updating Survey:", {
				method: "PUT",
				url: `${Endpoint.SURVEYS}/${survey.id}`,
				payload
			})
			const response = await this._http.requestNewApi({
				method: "PUT",
				url: `${Endpoint.SURVEYS}/${survey.id}`,
				headers: { "Content-Type": "multipart/form-data" },
				data: payload
			})
			return SurveyModel.fromRawJson(response.data)
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEY_UPDATE_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async deleteSurvey(id: string): Promise<void> {
		try {
			await this._http.requestNewApi({
				method: "DELETE",
				url: Endpoint.SURVEYS.replace("{surveyId}", id)
			})
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEY_DELETE_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async getSurveys(): Promise<SurveyModel[]> {
		try {
			const response = await this._http.requestNewApi({
				method: "GET",
				url: Endpoint.SURVEYS
			})
			return response.data.map((survey: SurveyModel) => SurveyModel.fromRawJson(survey))
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEYS_FETCH_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async getSurveySections(): Promise<SurveySectionModel[]> {
		try {
			const response = await this._http.requestNewApi({
				method: "GET",
				url: Endpoint.SURVEY_SECTIONS
			})
			return response.data.map((surveySection: SurveySectionModel) =>
				SurveySectionModel.fromRawJson(surveySection)
			)
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEY_SECTIONS_FETCH_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async createSurveySection(surveySection: SurveySectionModel): Promise<SurveySectionModel> {
		try {
			const response = await this._http.requestNewApi({
				headers: { "Content-Type": "multipart/form-data" },
				method: "POST",
				url: Endpoint.SURVEY_SECTIONS,
				data: surveySection
			})
			return SurveySectionModel.fromRawJson(response.data)
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEY_SECTION_CREATE_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async updateSurveySection(surveySection: SurveySectionModel): Promise<SurveySectionModel> {
		try {
			const response = await this._http.requestNewApi({
				headers: { "Content-Type": "multipart/form-data" },
				method: "PUT",
				url: `${Endpoint.SURVEY_SECTIONS}/${surveySection.id}`,
				data: surveySection
			})
			return SurveySectionModel.fromRawJson(response.data)
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEY_SECTION_UPDATE_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async deleteSurveySection(id: number): Promise<void> {
		try {
			await this._http.requestNewApi({
				method: "DELETE",
				url: `${Endpoint.SURVEY_SECTIONS}/${id}`
			})
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEY_SECTION_DELETE_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async exportSurveys(surveyId: number): Promise<Blob> {
		try {
			const response = await this._http.requestNewApi({
				url: Endpoint.SURVEYS_EXPORT.replace(":id", surveyId.toString()),
				method: "GET",
				responseType: "blob"
			})
			return response.data
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEYS_EXPORT_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async exportSurveyOptions(surveyId: number): Promise<Blob> {
		try {
			const response = await this._http.requestNewApi({
				url: Endpoint.SURVEYS_EXPORT_OPTIONS.replace(":id", surveyId.toString()),
				method: "GET",
				responseType: "blob"
			})
			return response.data
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEYS_EXPORT_ERROR,
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async updateSurveyCompletion(
		request: UpdateSurveyCompletionRequest
	): Promise<UpdateSurveyCompletionResponse> {
		try {
			const formData = new FormData()
			formData.append("commentary", request.commentary)
			if (request.pdfFile) {
				formData.append("pdf", request.pdfFile)
			}

			const response = await this._http.requestNewApi({
				method: "PUT",
				url: `${Endpoint.SURVEYS}/completion/${request.completionId}`,
				headers: { "Content-Type": "multipart/form-data" },
				data: formData
			})
			return {
				success: true,
				message: response.data?.message || "Comment updated successfully"
			}
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEY_UPDATE_ERROR || "Error updating survey completion comment",
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async exportSurveyCommentaryPDF(surveyId: number, surveyTitle: string): Promise<void> {
		try {
			const response = await this._http.requestNewApi({
				method: "GET",
				url: `${Endpoint.SURVEYS}/${surveyId}/export/pdf`,
				responseType: "blob"
			})

			// Generar nombre descriptivo desde el frontend
			const cleanTitle = surveyTitle
				.replace(/[^a-zA-Z0-9\s-]/g, "")
				.replace(/\s+/g, " ")
				.trim()
			const filename = `${cleanTitle}-comentarios-report.pdf`

			const blob = new Blob([response.data], { type: "application/pdf" })
			const url = window.URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = url
			link.download = filename
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEYS_EXPORT_ERROR || "Error exporting survey commentary PDF",
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async exportSurveyUserPDF(
		surveyId: number,
		userId: number,
		surveyTitle: string,
		userEmail: string
	): Promise<void> {
		try {
			const response = await this._http.requestNewApi({
				method: "GET",
				url: `${Endpoint.SURVEYS}/${surveyId}/user/${userId}/export/pdf`,
				responseType: "blob"
			})

			const cleanTitle = surveyTitle
				.replace(/[^a-zA-Z0-9\s-]/g, "")
				.replace(/\s+/g, " ")
				.trim()
			const cleanEmail = userEmail.replace(/[^a-zA-Z0-9@.-]/g, "")
			const filename = `${cleanTitle}-${cleanEmail}-report.pdf`

			const blob = new Blob([response.data], { type: "application/pdf" })
			const url = window.URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = url
			link.download = filename
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			window.URL.revokeObjectURL(url)
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEYS_EXPORT_ERROR || "Error exporting user survey PDF",
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}

	public async sendSurveyEmail(
		surveyId: number,
		userId: number,
		surveyTitle: string,
		userEmail: string
	): Promise<{ success: boolean; message: string }> {
		try {
			const response = await this._http.requestNewApi({
				method: "POST",
				url: `${Endpoint.SURVEYS}/${surveyId}/user/${userId}/send-email`,
				data: {
					surveyTitle,
					userEmail
				}
			})
			return response.data
		} catch (error: any) {
			throw new SurveyError({
				errorMessage: SurveyError.SURVEYS_EXPORT_ERROR || "Error sending survey email",
				errorCode: error?.error_msg,
				httpStatus: error?.httpStatus
			})
		}
	}
}
